import PropTypes from 'prop-types';
import SpinnerButton from '../shared/SpinnerButton';
import DomainStatusField from './DomainStatusField';
import DomainWarmingNote from './DomainWarmingNote';
import mailIcon from '../../icons/mail.svg';
import {
    ROUTES,
    DEFAULT_PRO_DOMAIN,
    CUSTOM_DOMAIN_SSL_ACTIVE_STATUSES,
    SENDING_DOMAIN_STATES,
    SENDING_DOMAIN_PENDING_STATUSES,
    SENDING_DOMAIN_FAILED_STATUSES,
} from '../../utils/constants';

/**
 * @typedef {import('../../types').CurrentSite} CurrentSite
 * @typedef {import('../../types').DNSRecord} DNSRecord
 * @typedef {import('../../utils/constants').SendingDomainState} SendingDomainState
 */

/**
 * Determines the description text based on sending domain status
 * @param {SendingDomainState} sendingDomainStatus - The sending domain status
 * @returns {React.ReactNode} JSX element for the description
 */
function getSendingDomainDescription(sendingDomainStatus) {
    if (SENDING_DOMAIN_FAILED_STATUSES.includes(sendingDomainStatus)) {
        return (
            <p className="red">
                We've detected a problem with your sending domain that may impact email
                deliverability for your newsletter
            </p>
        );
    }

    if (sendingDomainStatus === SENDING_DOMAIN_STATES.PENDING) {
        return <p>Your custom sending domain is pending to be verified</p>;
    }

    return <p>The domain you use to send email newsletters</p>;
}

/**
 * Renders the verified/pending/failed sending domain status view
 * @param {object} props - Component props
 * @param {CurrentSite} props.currentSite - The current site data
 * @param {DNSRecord[]} props.dnsRecords - DNS records for the sending domain
 * @returns {React.ReactNode} JSX element for the verified sending domain view
 */
function VerifiedSendingDomainView({ currentSite, dnsRecords }) {
    return (
        <div className="box-domain box-wrap">
            <div className="box">
                <section className="staticfield">
                    <header>
                        <div className="staticfield-label">Custom Sending Domain</div>
                    </header>
                    {getSendingDomainDescription(currentSite?.sending_domain_status)}
                    <DomainStatusField
                        status={currentSite?.sending_domain_status}
                        domain={currentSite?.external_domain}
                        type="custom-sending-domain"
                        linkTo={ROUTES.SENDING_DOMAIN}
                        params={dnsRecords}
                    />

                    <DomainWarmingNote
                        domainWarming={Boolean(currentSite?.domain_warming)}
                        sendingDomainStatus={currentSite?.sending_domain_status}
                        sendingDomainUpdatedAt={currentSite?.sending_domain_status_updated_at}
                    />
                </section>
            </div>
        </div>
    );
}

VerifiedSendingDomainView.propTypes = {
    currentSite: PropTypes.shape({
        sending_domain_status: PropTypes.string,
        external_domain: PropTypes.string,
    }),
    dnsRecords: PropTypes.array,
};

/**
 * Renders the setup sending domain view (before verification)
 * @param {object} props - Component props
 * @param {CurrentSite} props.currentSite - The current site data
 * @param {object} props.buttonState - Button state for the setup button
 * @param {Function} props.onSetup - Handler for setting up the sending domain
 * @returns {React.ReactNode} JSX element for the setup sending domain view
 */
function SetupSendingDomainView({ currentSite, buttonState, onSetup }) {
    const isDefunct = currentSite?.sending_domain_status === SENDING_DOMAIN_STATES.DEFUNCT;
    const externalDomainWithoutWww = currentSite?.external_domain?.replace(/^www\./, '');

    return (
        <div className="box-domain box-wrap">
            <div className="box">
                <section className="staticfield">
                    <header>
                        <div className="staticfield-label">Custom Sending Domain</div>
                    </header>
                    {isDefunct ? (
                        <p className="red">
                            Your custom sending domain has been removed due to missing DNS records.
                        </p>
                    ) : (
                        <p>
                            Customize the from-address of your email newsletter with a custom
                            sending domain.
                        </p>
                    )}

                    <div className="sending-domain">
                        <div className="sending-domain-rapper">
                            <strong>Default sending domain</strong>
                            <div className="sending-domain-box">
                                <img src={mailIcon} className="domain-status-icon" alt="Email" />
                                <strong>From:</strong>
                                <span>
                                    {currentSite?.subdomain}@{DEFAULT_PRO_DOMAIN}
                                </span>
                            </div>
                        </div>

                        <div className="sending-domain-arrow">&rarr;</div>

                        <div className="sending-domain-rapper">
                            <strong>Custom sending domain</strong>
                            <div className="sending-domain-box">
                                <img src={mailIcon} className="domain-status-icon" alt="Email" />
                                <strong>From:</strong>
                                <span>name@{externalDomainWithoutWww}</span>
                            </div>
                        </div>
                    </div>

                    <SpinnerButton
                        onClick={onSetup}
                        className={buttonState.className}
                        html={buttonState.html}
                        disabled={buttonState.disabled}
                        data-test-btn="setup-sending-domain"
                    />
                </section>
            </div>
        </div>
    );
}

SetupSendingDomainView.propTypes = {
    currentSite: PropTypes.shape({
        subdomain: PropTypes.string,
        external_domain: PropTypes.string,
        sending_domain_status: PropTypes.string,
    }),
    buttonState: PropTypes.shape({
        className: PropTypes.string,
        html: PropTypes.string,
        disabled: PropTypes.bool,
    }).isRequired,
    onSetup: PropTypes.func.isRequired,
};

/**
 * Displays the custom sending domain section
 * Shows different views based on sending domain status:
 * - Setup view: when no sending domain is configured
 * - Status view: when sending domain is verified, pending, or failed
 * @param {object} props - Component props
 * @param {CurrentSite} props.currentSite - The current site data
 * @param {boolean} props.isStarterPlan - Whether the user is on Starter plan
 * @param {DNSRecord[]} props.dnsRecords - DNS records for the sending domain
 * @param {object} props.buttonState - Button state for the setup button
 * @param {Function} props.onSetup - Handler for setting up the sending domain
 */
function SendingDomainSection({ currentSite, isStarterPlan, dnsRecords, buttonState, onSetup }) {
    // Don't show sending domain section if:
    // - Custom domain is not active
    // - User is on Starter plan
    // - Already using a custom sending domain with a `CUSTOM` status
    const hasActiveCustomDomain = CUSTOM_DOMAIN_SSL_ACTIVE_STATUSES.includes(
        currentSite?.ssl_status,
    );
    const isCustomSendingDomainStatus =
        currentSite?.sending_domain_status === SENDING_DOMAIN_STATES.CUSTOM;

    if (!hasActiveCustomDomain || isStarterPlan || isCustomSendingDomainStatus) {
        return null;
    }

    const sendingDomainStatus = currentSite?.sending_domain_status;

    // DEFUNCT is a special case - it shows the setup view with a special message,
    // not the verified/failed status view
    const isDefunct = sendingDomainStatus === SENDING_DOMAIN_STATES.DEFUNCT;
    const isVerified = sendingDomainStatus === SENDING_DOMAIN_STATES.VERIFIED;
    const isPending = sendingDomainStatus === SENDING_DOMAIN_STATES.PENDING;
    const isFailed = SENDING_DOMAIN_FAILED_STATUSES.includes(sendingDomainStatus) && !isDefunct;

    // Show status view for verified, pending, or failed states (but NOT defunct)
    const hasVerifiedOrPendingOrFailed = isVerified || isPending || isFailed;

    if (hasVerifiedOrPendingOrFailed) {
        return <VerifiedSendingDomainView currentSite={currentSite} dnsRecords={dnsRecords} />;
    }

    // Show setup view for no status, initialized, none, or defunct states
    const showSetupView =
        !sendingDomainStatus ||
        SENDING_DOMAIN_PENDING_STATUSES.includes(sendingDomainStatus) ||
        isDefunct;

    if (showSetupView) {
        return (
            <SetupSendingDomainView
                currentSite={currentSite}
                buttonState={buttonState}
                onSetup={onSetup}
            />
        );
    }

    return null;
}

SendingDomainSection.propTypes = {
    currentSite: PropTypes.shape({
        ssl_status: PropTypes.string,
        sending_domain_status: PropTypes.string,
        subdomain: PropTypes.string,
        external_domain: PropTypes.string,
    }),
    isStarterPlan: PropTypes.bool,
    dnsRecords: PropTypes.array,
    buttonState: PropTypes.shape({
        className: PropTypes.string,
        html: PropTypes.string,
        disabled: PropTypes.bool,
    }).isRequired,
    onSetup: PropTypes.func.isRequired,
};

export default SendingDomainSection;
